import { TFunction } from 'i18next';

import { IAuthUser } from 'auth/types';
import { SpaStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IVendorQuote } from 'quotes/types';

import {
  IBulkSendAsSpaResult,
  IBulkSendAsSpaSelection,
  IBulkSendAsSpaStatusCount,
  ISendAsSpaResult,
} from './types';

// Fail-closed: a non-imported row is omitted; a sent quote the BE does not flag resendable is
// skipped; one failing the Attributes preflight blocks the batch (resends included — the BE runs
// the same preflight on them); the rest are first sends or resends. The sent check precedes the
// preflight so a sent-but-incomplete quote lands in alreadySent, not blockers.
export const categorizeBulkSendAsSpaSelection = (
  quotes: IVendorQuote[],
  user?: IAuthUser | null
): IBulkSendAsSpaSelection => {
  const selection: IBulkSendAsSpaSelection = {
    alreadySent: [],
    blockers: [],
    firstSends: [],
    omits: [],
    resends: [],
  };

  const spaAudienceRights = VendorQuoteHelper.getSpaAudienceRights(user);

  (quotes ?? []).forEach((quote) => {
    const isResend = quote.canResendAsSpa === true;

    if (!VendorQuoteHelper.isSkuExportAvailable({ quote, user })) {
      selection.omits.push(quote);
    } else if (!isResend && VendorQuoteHelper.isTradeAgreementSent(quote.tradeAgreementStatus)) {
      selection.alreadySent.push(quote);
    } else if (null !== VendorQuoteHelper.getSpaSendBlockReason(quote, spaAudienceRights)) {
      selection.blockers.push(quote);
    } else if (isResend) {
      selection.resends.push(quote);
    } else {
      selection.firstSends.push(quote);
    }
  });

  return selection;
};

export const getBulkSendAsSpaButtonLabelKey = ({
  firstSends,
  resends,
}: Pick<IBulkSendAsSpaSelection, 'firstSends' | 'resends'>): string => {
  if (resends.length === 0) {
    return 'buttonLabelBulkSendAsSpa';
  }

  if (firstSends.length === 0) {
    return 'buttonLabelBulkResendAsSpa';
  }

  return 'buttonLabelBulkSendAndResendAsSpa';
};

// A resend ends a live agreement and a past Valid Until needs the user's word, so either opens the
// dialog; so does anything that will not be sent.
export const isBulkSendAsSpaConfirmationNeeded = (
  { blockers, omits, alreadySent, resends }: Omit<IBulkSendAsSpaSelection, 'firstSends'>,
  pastValidUntilQuoteIds: number[]
): boolean =>
  blockers.length + omits.length + alreadySent.length + resends.length > 0 ||
  pastValidUntilQuoteIds.length > 0;

// Per-status counts of the quotes a bulk send includes, each going out with its own stored
// status (null or unknown goes out as Planning, as in mapAttributesToForm). Planning first; a
// status no quote carries is left out.
export const getBulkSendAsSpaStatusCounts = (
  quotes: Pick<IVendorQuote, 'attributeSpaStatus'>[]
): IBulkSendAsSpaStatusCount[] =>
  [SpaStatus.PLANNING, SpaStatus.LIVE]
    .map((status) => ({
      status,
      count: (quotes ?? []).filter(
        (quote) =>
          (quote.attributeSpaStatus === SpaStatus.LIVE ? SpaStatus.LIVE : SpaStatus.PLANNING) ===
          status
      ).length,
    }))
    .filter(({ count }) => count > 0);

// Sentences of the bulk success toast, in order: first sends, resends, quotes another job had
// already sent, quotes another send held locked, resends the BE could not version at enqueue time.
// Zero counts are left out.
export const getBulkSendAsSpaSuccessMessages = ({
  result,
  droppedCount,
  fallbackSentCount,
}: {
  result?: IBulkSendAsSpaResult | null;
  droppedCount: number;
  fallbackSentCount: number;
}): { key: string; count: number }[] => {
  const sentCount = result?.quoteIds?.length ?? fallbackSentCount;
  const resentCount = result?.resendQuoteIds?.length ?? 0;
  const skippedCount = result?.skippedQuoteIds?.length ?? 0;
  const lockFailedCount = result?.lockFailedQuoteIds?.length ?? 0;

  return [
    { key: 'messageBulkSendAsSpaSuccess', count: Math.max(sentCount - resentCount, 0) },
    { key: 'messageBulkResendAsSpaSuccess', count: resentCount },
    { key: 'messageBulkSendAsSpaSkippedByOtherSend', count: skippedCount },
    { key: 'messageBulkSendAsSpaLockFailed', count: lockFailedCount },
    { key: 'messageBulkSendAsSpaDropped', count: droppedCount },
  ].filter(({ count }) => count > 0);
};

// A single send that uploaded nothing because another send owned the quote: still running (lock
// not obtained) or already done (skipped). Null for a real failure, which keeps the BE error.
export const getSendAsSpaConcurrentSendNotice = (
  result?: ISendAsSpaResult | null
): { key: string; isWarning: boolean } | null => {
  if ((result?.lockFailedQuoteIds?.length ?? 0) > 0) {
    return { key: 'messageSendAsSpaLockFailed', isWarning: false };
  }

  if ((result?.skippedQuoteIds?.length ?? 0) > 0) {
    return { key: 'messageSendAsSpaSkippedByOtherSend', isWarning: true };
  }

  return null;
};

// A bulk send that uploaded nothing because other sends owned every quote: the counted reasons,
// as an error when any quote was still locked, else a warning. Null for a real failure, which
// keeps the BE error.
export const getBulkSendAsSpaConcurrentSendNotice = (
  result?: IBulkSendAsSpaResult | null
): { messages: { key: string; count: number }[]; isWarning: boolean } | null => {
  const skippedCount = result?.skippedQuoteIds?.length ?? 0;
  const lockFailedCount = result?.lockFailedQuoteIds?.length ?? 0;

  if (skippedCount + lockFailedCount === 0) {
    return null;
  }

  return {
    messages: [
      { key: 'messageBulkSendAsSpaSkippedByOtherSend', count: skippedCount },
      { key: 'messageBulkSendAsSpaLockFailed', count: lockFailedCount },
    ].filter(({ count }) => count > 0),
    isWarning: lockFailedCount === 0,
  };
};

// Ascending by id; a quote missing from `quotes` or without a number is named `#<id>`.
export const formatVendorQuoteNames = (
  quoteIds: number[],
  quotes: Pick<IVendorQuote, 'id' | 'vendorQuoteNumber'>[]
): string =>
  [...quoteIds]
    .sort((a, b) => a - b)
    .map((id) => quotes.find((quote) => quote.id === id)?.vendorQuoteNumber || `#${id}`)
    .join(', ');

// A send FlintFox accepted whose status write was rolled back: an error, never a success, and it
// outranks the skipped / lock-failed notices. The BE text says not to send again, so callers must
// not offer a retry. Bulk prefixes the affected quotes, named via formatVendorQuoteNames. Null when
// this did not happen.
export const getSendAsSpaStatusWriteFailedMessage = ({
  result,
  isBulk,
  quotes = [],
  t,
}: {
  result?: Pick<ISendAsSpaResult, 'sent' | 'error' | 'statusWriteFailedQuoteIds'> | null;
  isBulk: boolean;
  quotes?: Pick<IVendorQuote, 'id' | 'vendorQuoteNumber'>[];
  t: TFunction;
}): string | null => {
  const quoteIds = result?.statusWriteFailedQuoteIds ?? [];

  if (result?.sent !== true || quoteIds.length === 0) {
    return null;
  }

  const message = result.error || t('messageSendAsSpaStatusWriteFailed');

  return isBulk
    ? t('messageBulkSendAsSpaStatusWriteFailed', {
        count: quoteIds.length,
        quotes: formatVendorQuoteNames(quoteIds, quotes),
        message,
      })
    : message;
};
